/**
 * Organizer & Shop API types.
 * Backend DTO'larını mirror'lar. Backend'de değişiklik olunca buraları güncelle.
 */

/* ============================================================================
 * i18n + Currency — platform-wide sabitler. Backend SupportedLocales /
 * SupportedCurrencies ile senkron. Dil/currency eklerken iki tarafı da güncelle.
 * ========================================================================= */

export const SUPPORTED_LOCALES = ["tr", "en", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "tr";

/** RTL (right-to-left) olan diller. */
export const RTL_LOCALES: readonly Locale[] = ["ar"];

export function isRtlLocale(locale: Locale): boolean {
  return RTL_LOCALES.includes(locale);
}

export function getLocaleDir(locale: Locale): "ltr" | "rtl" {
  return isRtlLocale(locale) ? "rtl" : "ltr";
}

export const SUPPORTED_CURRENCIES = ["TRY", "EUR", "USD"] as const;
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];
export const DEFAULT_CURRENCY: CurrencyCode = "TRY";

export type OrganizerStatus =
  | "PendingEmailVerification"
  | "Active"
  | "Suspended"
  | "Deleted";

export type ShopStatus = "Active" | "Paused" | "Archived";

/**
 * Storefront hero (üst banner) layout preset'i.
 * - `Profile`: kimlik-odaklı, kapak + sol alt avatar + isim.
 * - `Showcase`: görsel-odaklı, yüksek kapak + koyu gradient + üstte büyük isim.
 *
 * Yalnızca `storefrontType === "Site"` için anlamlıdır; Link/Embed tiplerinde
 * TN markalı şablon kullanılır ve bu field göz ardı edilir.
 */
export type ShopHeroStyle = "Profile" | "Showcase";

/**
 * Mağaza tipi — render path'i belirler.
 * - `Link`: TN markalı sade şablon. Onboarding default'u. "Sadece bilet satayım" diyenler.
 * - `Embed`: Link gibi render + dashboard'da embed snippet panel + iframe route aktif.
 *            Public URL Link tipi gibi davranır; fark organizer'a açılan ek yetenek.
 * - `Site`: Mevcut zengin mini-site (cover, hero style, sections). Brand'ini önemseyen.
 *
 * Mevcut shop'lar migration'da Site olarak işaretlendi; yeni shop'lar Link default'u alır.
 */
export type ShopStorefrontType = "Link" | "Embed" | "Site";

export interface ShopSummary {
  id: string;
  slug: string;
  name: string;
  status: ShopStatus;
  defaultLocale: Locale;
}

/**
 * Shop detay — /shops ve /shops/{slug} endpoint'lerinin dönüş tipi.
 * Currency burada yok: her TicketType kendi currency'sini tutar (shop currency-agnostic).
 */
export interface Shop {
  id: string;
  organizerId: string;
  slug: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  themeColor: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: ShopStatus;
  defaultLocale: Locale;
  heroStyle: ShopHeroStyle;
  storefrontType: ShopStorefrontType;
  /**
   * Embed iframe'in döndürebileceği "tn_return" URL'lerinde izinli host
   * listesi. Format: host-only (port veya path olmadan), ör.
   * <c>["konser-sitem.com", "blog.konser-sitem.com"]</c>. Subdomain match
   * destekli; whitelist boşsa "Siteye Dön" CTA'sı pasif olur (sipariş
   * yine başarılı, sadece full-circle UX kapalı).
   */
  allowedEmbedDomains: string[];
  tracking: ShopTracking;
  createdAt: string;
  updatedAt: string;
}

/**
 * Mağaza izleme kimlikleri. Shop'ta yalnız alıcı çerez onayı verince yüklenir;
 * kart bilgisinin girildiği checkout sayfasında hiç yüklenmez.
 */
export interface ShopTracking {
  /** `GTM-XXXXXXX` */
  gtmContainerId: string | null;
  /** `G-XXXXXXXXXX` */
  ga4MeasurementId: string | null;
  /** Yalnız rakam */
  metaPixelId: string | null;
}

/** PUT /shops/{slug}/tracking — üç alan birlikte yazılır, boş/null alan kapatılır. */
export type UpdateShopTrackingRequest = ShopTracking;

/** Public mağaza yanıtındaki izleme ayarı. */
export interface PublicShopTracking extends ShopTracking {
  /** Satın alma Meta'ya sunucudan da bildirilir (Pixel + CAPI anahtarı kayıtlı). */
  metaCapi: boolean;
}

/** GET/PUT/DELETE /shops/{slug}/tracking/meta-capi — erişim anahtarı hiçbir yanıtta dönmez. */
export interface MetaCapiSettings {
  /** Sunucuda şifreleme anahtarı tanımlı; değilse bu ortamda CAPI kapalı. */
  available: boolean;
  configured: boolean;
  testEventCode: string | null;
  lastDelivery: MetaCapiDelivery | null;
}

export interface MetaCapiDelivery {
  status: "Sent" | "Failed";
  orderNumber: string;
  at: string;
  error: string | null;
}

/** Boş/null `accessToken` kayıtlı anahtarı korur (yalnız test kodu değişir). */
export interface UpdateMetaCapiRequest {
  accessToken?: string | null;
  testEventCode?: string | null;
}

export interface CreateShopRequest {
  name: string;
  description?: string | null;
  slug?: string | null;
  themeColor?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  /** Storefront default dili. Boşsa organizer'ın PreferredLocale'i. */
  defaultLocale?: Locale | null;
  /** Storefront hero layout. Null gelirse backend "Profile" default'u uygular. */
  heroStyle?: ShopHeroStyle | null;
  /** Mağaza tipi. Null gelirse backend "Link" default'u uygular (onboarding). */
  storefrontType?: ShopStorefrontType | null;
}

export interface UpdateShopRequest {
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  coverImageUrl?: string | null;
  themeColor?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  defaultLocale?: Locale | null;
  /** Storefront hero layout. Null gelirse backend mevcut değeri korur. */
  heroStyle?: ShopHeroStyle | null;
  /** Mağaza tipi. Null gelirse mevcut değer korunur. */
  storefrontType?: ShopStorefrontType | null;
  /**
   * Embed iframe'in döndürebileceği "tn_return" host listesi. null ise
   * mevcut değer korunur; boş array gönderilirse temizlenir. Backend
   * normalize eder (lowercase, host-only, max 20 entry).
   */
  allowedEmbedDomains?: string[] | null;
}

export interface SignupRequest {
  email: string;
  password: string;
  companyName: string;
  phoneNumber: string;
  /** UI'nın açıldığı dil. Boşsa backend Accept-Language'dan alır. */
  preferredLocale?: Locale | null;
}

export interface SignupResponse {
  organizerId: string;
  email: string;
  companyName: string;
  status: OrganizerStatus;
  defaultShopId: string;
  defaultShopSlug: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Sadece organizer logosunu günceller. null/boş string → mevcut logo kaldırılır.
 */
export interface UpdateOrganizerLogoRequest {
  logoUrl: string | null;
}

/**
 * Yasal bilgi formu. Tüm alanlar zorunlu — Shop'un Active'e alınabilmesi için
 * eksiksiz olmalı (backend guard). Backend ayrıca format validasyonu yapar.
 */
export interface UpdateOrganizerLegalInfoRequest {
  /** Resmi firma unvanı (ticari ünvan). KVKK veri sorumlusu olarak gözükür. */
  legalName: string;
  /** TCKN (11) veya VKN (10). Salt rakam. */
  taxNumber: string;
  /** Vergi dairesi. Mesafeli satış sözleşmesinde taraf bilgisi. */
  taxOffice: string;
  /** Fatura / yasal yazışma adresi. */
  billingAddress: string;
}

export type MemberRole = "Owner" | "Manager" | "Editor" | "Accountant" | "Gate";
export type MemberStatus = "Invited" | "Active" | "Disabled";

export interface OrganizerSessionResponse {
  organizerId: string;
  email: string;
  companyName: string;
  /** Login olan member'ın id'si. Multi-user takım yapısı. */
  memberId: string;
  /** Login olan member'ın rolü. Dashboard menü/route guard kullanır. */
  role: MemberRole;
  emailVerified: boolean;
  phoneVerified: boolean;
  preferredLocale: Locale;
  /** Platform'un bu organizer'a uyguladığı komisyon oranı (%). Read-only. Owner/Manager/Accountant dışında 0. */
  commissionRate: number;
  /** Bilet başına sabit servis ücreti. Read-only. */
  serviceFee: number;
  /** Organizer logosu — dashboard header + storefront'ta görünür. */
  logoUrl: string | null;
  /** Resmi firma unvanı. Yasal sayfalarda veri sorumlusu olarak kullanılır. Owner dışında null. */
  legalName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  billingAddress: string | null;
  /**
   * Tüm yasal alanlar (legalName + taxNumber + taxOffice + billingAddress) dolu mu?
   * Backend hesaplar; false ise shop Active'e alınamaz.
   */
  isLegalInfoComplete: boolean;
  shops: ShopSummary[];
  /**
   * TicketNjoy destek ekibi bu oturumu admin panelinden açtıysa dolu. Dashboard üstte
   * uyarı bandı gösterir; bu oturumdaki tüm işlemler kayıtlarda "TicketNjoy" olarak görünür.
   */
  impersonation?: OrganizerImpersonation | null;
}

export interface OrganizerImpersonation {
  /** UTC ISO. Oturum en fazla 1 saat açık kalır. */
  expiresAt: string;
}

// ---------- Team / Members ----------

export interface TeamMemberResponse {
  id: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  inviteExpiresAt: string | null;
  joinedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface InviteMemberRequest {
  email: string;
  role: MemberRole;
}

export interface UpdateMemberRoleRequest {
  role: MemberRole;
}

export interface AcceptMemberInviteRequest {
  token: string;
  password: string;
}

// ---------- Check-in (QR PWA) ----------

/** Backend ScanResult enum string serialization. */
export type ScanResult =
  | "Success"
  | "AlreadyUsed"
  | "Cancelled"
  | "NotFound"
  | "NotAuthorized"
  | "WrongEvent";

/** Ticket statüsü — Orders feature'ından da kullanılır. */
export type TicketStatus = "Valid" | "Used" | "Cancelled";

export interface ScanTicketRequest {
  /** Ham QR içeriği veya elle yazılmış kod. Server normalize eder. */
  ticketCode: string;
  /** Opsiyonel — kasiyerin "etkinlik X için tarıyorum" filtresi. */
  eventId?: string | null;
}

export interface ScanTicketInfo {
  ticketId: string;
  ticketCode: string;
  eventId: string;
  eventTitle: string;
  ticketTypeName: string;
  buyerName: string;
  orderNumber: string;
  usedAt: string | null;
  status: TicketStatus;
  /** Bileti kapıda okutan team member'ın email snapshot'ı. AlreadyUsed cevabında doludur. */
  checkedInByEmail: string | null;
}

export interface ScanTicketResponse {
  result: ScanResult;
  message: string;
  ticket: ScanTicketInfo | null;
}

export interface CheckInEventListItem {
  id: string;
  title: string;
  startsAt: string;
  venueName: string | null;
  totalTickets: number;
  checkedInTickets: number;
}

export interface CheckInEventStats {
  eventId: string;
  title: string;
  startsAt: string;
  totalTickets: number;
  checkedInTickets: number;
  cancelledTickets: number;
}

// ---------- Uploads ----------

export type UploadKind =
  | "EventCover"
  | "ShopLogo"
  | "ShopCover"
  | "OrganizerLogo";

export interface UploadResponse {
  /** Public, CDN'e servis edilen mutlak URL. DB'ye bu yazılır. */
  url: string;
  /** Bucket içindeki tam key (prefix dahil). İleride delete için saklanabilir. */
  key: string;
}

// ---------- Event ----------

export type EventStatus =
  | "Draft"
  | "Published"
  | "Paused"
  | "Cancelled"
  | "Ended"
  | "Archived";

export interface TicketType {
  id: string;
  eventId: string;
  name: string;
  description: string | null;
  price: number;
  /** Her ticket kendi currency'sinde fiyatlanır. Shop currency-agnostic. */
  currency: CurrencyCode;
  quantity: number;
  sold: number;
  available: number;
  /** Satış penceresi — null ise event publish anından itibaren satışa açık. */
  salesStart: string | null;
  salesEnd: string | null;
  /** Sepet başına adet sınırları. */
  minPerOrder: number;
  maxPerOrder: number | null;
  /** KDV oranı (%) — null ise event.defaultTaxRate fallback. */
  taxRate: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: string;
  organizerId: string;
  slug: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  /** WGS84 enlem. Dashboard'daki harita picker set eder. */
  latitude: number | null;
  /** WGS84 boylam. */
  longitude: number | null;
  startsAt: string;
  endsAt: string;
  /** Event default KDV oranı (%). TicketType.taxRate null ise bu kullanılır. */
  defaultTaxRate: number;
  status: EventStatus;
  publishedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  /** Event-level embed iframe açık mı (default true). */
  embedEnabled: boolean;
  /**
   * Custom embed slug — organizer tanımladığı kısa URL. null ise embed
   * loader event.slug'ı kullanır. Organizer scope'unda unique.
   */
  embedSlug: string | null;
  /** Iframe default açılış modu (Compact = teaser, Inline = ticket selector hep açık). */
  embedMode: EmbedMode;
  ticketTypes: TicketType[];
  createdAt: string;
  updatedAt: string;
}

export interface EventSummary {
  id: string;
  slug: string;
  title: string;
  coverImageUrl: string | null;
  venueCity: string | null;
  startsAt: string;
  endsAt: string;
  status: EventStatus;
  ticketTypeCount: number;
  totalCapacity: number;
  totalSold: number;
}

export interface CreateEventRequest {
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  slug?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  /** WGS84 enlem/boylam. Dashboard haritadan seçince set edilir. */
  latitude?: number | null;
  longitude?: number | null;
  startsAt: string;
  endsAt: string;
  /** Event default KDV oranı (%). 0-100 arası. Default 20. */
  defaultTaxRate?: number;
}

export interface UpdateEventRequest {
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  startsAt: string;
  endsAt: string;
  defaultTaxRate?: number;
  // ---- Embed config (event-level) ----
  embedEnabled?: boolean;
  /**
   * Custom embed slug — null/undefined → temizle (event.slug fallback).
   * Backend kabul kalıbı: ^[a-z0-9]+(?:-[a-z0-9]+)*$, 2-100 char.
   * Organizer scope'unda unique; çakışma 409 conflict.
   */
  embedSlug?: string | null;
  embedMode?: EmbedMode;
}

export interface CancelEventRequest {
  reason?: string | null;
}

/** İptal öncesi etki: ödenmiş siparişler otomatik iade edilir. */
export interface EventCancelPreview {
  orderCount: number;
  ticketCount: number;
  refundAmount: number;
  currency: CurrencyCode;
  /** Satış varsa yalnız iade yetkisi olan (Owner / Manager) iptal edebilir. */
  canCancel: boolean;
}

/** Başarısız iade; otomatik yeniden denenmez, panelden "tekrar dene" bekler. */
export interface EventCancellationRefundProblem {
  orderNumber: string;
  buyerName: string;
  attempts: number;
  lastError: string | null;
}

export interface EventCancellationRefundSummary {
  total: number;
  succeeded: number;
  /** Zaten iade edilmiş / iade edilecek bilet kalmamış siparişler. */
  skipped: number;
  pending: number;
  failed: number;
  problems: EventCancellationRefundProblem[];
}

export interface CreateTicketTypeRequest {
  name: string;
  description?: string | null;
  price: number;
  currency: CurrencyCode;
  quantity: number;
  /** Satış penceresi — ISO 8601 UTC. Null/undefined → event publish anından itibaren satışta. */
  salesStart?: string | null;
  salesEnd?: string | null;
  /** Sepet başına min/max adet. */
  minPerOrder?: number;
  maxPerOrder?: number | null;
  /** KDV oranı (%) — null → event.defaultTaxRate fallback. */
  taxRate?: number | null;
  sortOrder?: number;
}

export interface UpdateTicketTypeRequest {
  name: string;
  description?: string | null;
  price: number;
  currency: CurrencyCode;
  quantity: number;
  salesStart?: string | null;
  salesEnd?: string | null;
  minPerOrder?: number;
  maxPerOrder?: number | null;
  taxRate?: number | null;
  sortOrder?: number;
}

// ---------- ShopEvent (Shop ↔ Event join) ----------

export type ShopEventStatus = "Listed" | "Hidden";

/**
 * Bir ShopEvent altında satılmak üzere seçilen TicketType'ın özet bilgisi.
 * Dashboard "bu mağazada hangi biletler satılıyor" listesi için.
 */
export interface ShopEventTicketInfo {
  ticketTypeId: string;
  name: string;
  price: number;
  currency: CurrencyCode;
  quantity: number;
  sold: number;
}

export interface ShopEvent {
  id: string;
  shopId: string;
  eventId: string;
  status: ShopEventStatus;
  section: string | null;
  displayOrder: number;
  eventSlug: string;
  eventTitle: string;
  eventCoverImageUrl: string | null;
  eventVenueCity: string | null;
  eventStartsAt: string;
  eventEndsAt: string;
  eventStatus: EventStatus;
  eventEmbedEnabled: boolean;
  /** Null → embed kodu canonical event slug'ını kullanır. */
  eventEmbedSlug: string | null;
  eventEmbedMode: EmbedMode;
  /** Bu shop'ta bu event için satılacak ticket type listesi. */
  attachedTickets: ShopEventTicketInfo[];
  createdAt: string;
  updatedAt: string;
}

export interface AttachEventToShopRequest {
  eventId: string;
  /** Bu shop'ta satılacak ticket type'lar. En az 1 zorunlu. */
  ticketTypeIds: string[];
  section?: string | null;
  displayOrder?: number;
}

/**
 * ShopEvent metadata update — section + display order. Status değişmez;
 * görünürlük için POST /shops/{slug}/events/{id}/show|hide endpoint'leri var.
 */
export interface UpdateShopEventMetadataRequest {
  section?: string | null;
  displayOrder: number;
}

export interface UpdateShopEventTicketsRequest {
  ticketTypeIds: string[];
}

// ---------- Public (storefront) ----------

export interface PublicShop {
  slug: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  themeColor: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  defaultLocale: Locale;
  heroStyle: ShopHeroStyle;
  /**
   * Storefront davranış modeli. Frontend buna göre render path'i seçer:
   *   "Link" / "Embed" → TN markalı şablon (LinkLayout/LinkHeader)
   *   "Site"           → Mevcut zengin mini-site (ShopHero + sections)
   */
  storefrontType: ShopStorefrontType;
  tracking: PublicShopTracking;
}

/**
 * Event kartı fiyat aralığı + currency. Shop currency-agnostic olduğu için
 * her event kartı kendi currency'sini taşır.
 */
export interface PublicPriceRange {
  min: number;
  max: number;
  currency: CurrencyCode;
}

export interface PublicEventListItem {
  slug: string;
  title: string;
  coverImageUrl: string | null;
  venueName: string | null;
  venueCity: string | null;
  startsAt: string;
  endsAt: string;
  /** Fiyat aralığı + currency. Null → ticket yok veya quantity 0. */
  priceRange: PublicPriceRange | null;
  section: string | null;
  displayOrder: number;
  isSoldOut: boolean;
}

export interface PublicTicketType {
  id: string;
  name: string;
  description: string | null;
  /** Taban fiyat — breakdown için ham değer. Storefront ana gösterimi `totalPrice`. */
  price: number;
  /** Alıcıya gösterilen brüt fiyat: base + KDV + servis. "KDV & servis dahil". */
  totalPrice: number;
  /** Bilet başına sabit servis ücreti (breakdown'da gösterilir). */
  serviceFee: number;
  /** Uygulanan KDV oranı (%). */
  taxRate: number;
  /** Her ticket kendi currency'sinde. */
  currency: CurrencyCode;
  available: number;
  isSoldOut: boolean;
  salesStart: string | null;
  salesEnd: string | null;
  minPerOrder: number;
  maxPerOrder: number | null;
  /** Bu bilet şu an satışta mı (window + event status). */
  isOnSale: boolean;
  sortOrder: number;
}

export interface PublicEventDetail {
  slug: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  /** Storefront küçük harita + yol tarifi linki için. */
  latitude: number | null;
  longitude: number | null;
  startsAt: string;
  endsAt: string;
  /** En az bir ticket satışta mı. */
  isOnSale: boolean;
  isSoldOut: boolean;
  /**
   * Bu shop için aktif + ortamda görünür bir ödeme yöntemi tanımlı mı.
   * false ise sepete ekleme + checkout disable edilmeli — satıcı henüz
   * Param/Wallet vb. seçmemiş.
   */
  canAcceptPayments: boolean;
  ticketTypes: PublicTicketType[];
  shop: PublicShop;
  /**
   * Aktif otomatik promosyonlar (code=null). Storefront event detayında badge olarak gösterilir.
   * Kodlu kuponlar burada değildir — kullanıcı checkout'ta kodu girdiğinde devreye girer.
   */
  activePromotions: PublicActivePromotion[];
}

/** Iframe widget'ın default açılış modu. */
export type EmbedMode = "Compact" | "Inline";

/**
 * Embed iframe sayfasının response zarfı — event detayı + embed metadata.
 * Sadece <c>EmbedEnabled = true</c> olan event'ler döner; aksi halde 404.
 */
export interface PublicEmbedEvent {
  event: PublicEventDetail;
  /** Default mode — loader script "data-tn-mode" ile override edebilir. */
  embedMode: EmbedMode;
  /** Custom embed slug; null ise canonical event slug kullanılıyor demektir. */
  embedSlug: string | null;
}

/**
 * Yasal sayfa slug sabitleri — backend LegalPageSlugs sınıfıyla senkron.
 * URL'de ve storefront footer'ında bu string'ler kullanılır.
 */
export const LEGAL_PAGE_SLUGS = [
  "kvkk",
  "gizlilik",
  "kullanim-kosullari",
  "mesafeli-satis",
  "iade-iptal",
] as const;
export type LegalPageSlug = (typeof LEGAL_PAGE_SLUGS)[number];

/**
 * Storefront yasal sayfa render yanıtı. Backend template'i organizer
 * bilgileriyle doldurmuş veya override varsa onu geri vermiş olur.
 */
export interface PublicLegalPage {
  slug: string;
  title: string;
  /** Render edilmiş HTML — storefront doğrudan dangerouslySetInnerHTML ile basar. */
  contentHtml: string;
  /** Organizer override'ı devrede mi (sayfa platform şablonu değil mi). */
  isOverridden: boolean;
  /** ISO UTC timestamp. Override varsa override.updatedAt, yoksa template generation time. */
  lastUpdatedAt: string;
}

/**
 * Dashboard override editörü için detay yanıtı. Platform şablonunun ham Markdown'ı
 * ("eski değer"e dönüş için referans) + organizer override'ı (varsa).
 */
export interface LegalOverrideDetail {
  slug: string;
  title: string;
  /** Platform template'inin Markdown kaynağı — editör "default" görünümü için. */
  defaultTemplateMarkdown: string;
  /** Organizer override Markdown'ı; override yoksa null. */
  overrideMarkdown: string | null;
  /** Override varsa son güncelleme timestamp'i. */
  overrideUpdatedAt: string | null;
}

/**
 * Override kaydet/güncelle payload'ı. Boş string → override silinir, template'e döner.
 * İçerik Markdown; backend render sırasında sanitize eder.
 */
export interface UpsertLegalOverrideRequest {
  contentMarkdown: string;
}

/** Storefront event detayında gösterilecek otomatik kampanya özeti. */
export interface PublicActivePromotion {
  id: string;
  name: string;
  description: string | null;
  type: PromotionTypeValue;
  buyX: number | null;
  getY: number | null;
  discountValue: number | null;
  discountCurrency: CurrencyCode | null;
  endsAt: string | null;
  /** null → event'in tüm biletleri kapsam içinde. Doluysa sadece bu id'ler. */
  ticketTypeIds: string[] | null;
}

// ---------- Orders ----------

export type OrderStatus =
  | "Pending"
  | "Paid"
  | "Cancelled"
  | "Expired"
  | "Refunded"
  | "PartiallyRefunded";
export type TicketStatusValue = "Valid" | "Used" | "Cancelled";

export interface OrderItemRequestDto {
  ticketTypeId: string;
  quantity: number;
}

export interface CreateOrderRequest {
  shopSlug: string;
  eventSlug: string;
  items: OrderItemRequestDto[];
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerPhone: string;
  /** Opsiyonel kupon kodu. Boşsa otomatik promolar uygulanır. */
  couponCode?: string | null;
}

/** Preview: sepet fiyat kırılımı — sipariş oluşturmaz, stok düşürmez. */
export interface PreviewOrderRequest {
  shopSlug: string;
  eventSlug: string;
  items: OrderItemRequestDto[];
  couponCode?: string | null;
}

export interface AppliedPromotionResponse {
  promotionId: string;
  name: string;
  type: PromotionTypeValue;
  codeUsed: string | null;
  discountAmount: number;
  freeQuantity: number;
}

export interface PreviewOrderResponse {
  currency: string;
  subtotal: number;
  taxTotal: number;
  serviceFeeTotal: number;
  discountTotal: number;
  freeQuantityTotal: number;
  total: number;
  items: OrderItemResponse[];
  appliedPromotion: AppliedPromotionResponse | null;
  /** Kullanıcı kod girdi ama uygulanmadı — UI "kod geçersiz" uyarısı gösterir. */
  couponInvalid: boolean;
}

export interface OrderItemResponse {
  id: string;
  ticketTypeId: string;
  ticketTypeName: string;
  quantity: number;
  /** Taban fiyat per unit (KDV ve servis hariç). Catalog snapshot, indirim öncesi. */
  unitPrice: number;
  /** Per unit effective KDV tutarı (indirim sonrası base üzerinden). */
  taxAmount: number;
  /** Per unit servis ücreti. IsFree=true ise 0. */
  serviceFee: number;
  /** Uygulanan KDV oranı (%). */
  taxRate: number;
  /** Line base total effective = unitPrice*quantity - discountAmount. */
  subtotal: number;
  /** BOGO'dan gelen bedava QR satırı mı? True ise tüm fiyat alanları 0. */
  isFree: boolean;
  /** Bu satırı doğuran/etkileyen promosyon. Null → promo yok. */
  promotionId: string | null;
  /** Satıra düşen indirim tutarı (Percentage/FixedAmount). BOGO için 0. */
  discountAmount: number;
}

export interface OrderTicketResponse {
  id: string;
  ticketCode: string;
  ticketTypeName: string;
  status: TicketStatusValue;
  /** Bu bileti üreten OrderItem id'si — refund modal'ı tutar hesabında kullanır. */
  orderItemId: string;
}

/**
 * POST /public/orders response. Pending durumunda, biletler henüz yok.
 */
export interface PendingOrderResponse {
  orderNumber: string;
  status: OrderStatus;
  total: number;
  currency: string;
  expiresAt: string;
  items: OrderItemResponse[];
}

/**
 * Pay sonrası veya success sayfasında gösterilen detay.
 */
export interface OrderDetailResponse {
  orderNumber: string;
  status: OrderStatus;
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  /** Taban fiyat toplamı (indirim sonrası, KDV + servis hariç). */
  subtotal: number;
  /** Toplam KDV. */
  taxTotal: number;
  /** Toplam servis ücreti. */
  serviceFeeTotal: number;
  /** Promosyonlardan toplam indirim (transparency için). */
  discountTotal: number;
  /** BOGO'dan gelen toplam bedava QR sayısı. */
  freeQuantityTotal: number;
  /** @deprecated Legacy alias — serviceFeeTotal ile aynı. */
  serviceFee: number;
  /** Alıcının ödediği toplam = subtotal + taxTotal + serviceFeeTotal. */
  total: number;
  currency: string;
  paidAt: string | null;
  createdAt: string;
  eventTitle: string;
  eventSlug: string;
  eventVenueName: string | null;
  eventStartsAt: string;
  shopSlug: string;
  shopName: string;
  items: OrderItemResponse[];
  tickets: OrderTicketResponse[];
  appliedPromotions: AppliedPromotionResponse[];
  /**
   * Son başarısız ödeme denemesinin hata nedeni (ör. "Yetersiz bakiye").
   * Pending status'te kullanıcıya "banka dedi ki" olarak gösterilir; Paid'e
   * geçince yine dolu olabilir (tarihsel iz).
   */
  lastPaymentError?: string | null;
  /** Checkout'ta toplanan alıcı telefonu — SMS bildirimi için. */
  buyerPhone: string;
  /**
   * Sadece <c>Pending</c> siparişlerde dolu — sepet süresi geri sayımı
   * göstermek için. Diğer durumlarda <c>null</c>.
   */
  expiresAt: string | null;
  /**
   * Embed iframe'den başlatılan checkout için organizer site URL'i. Order
   * success page'inde "Siteye Dön" butonu için. Null ise embed kaynaklı değil
   * veya whitelist'ten geçmedi (return UX kapalı).
   */
  embedReturnUrl?: string | null;
  /** Bu sipariş için yapılmış toplam iade tutarı (birikimsel). */
  refundedAmount: number;
  /** İlk iade tarihi — hiç iade yapılmamışsa null. */
  firstRefundedAt: string | null;
  /** Son iade tarihi — hiç iade yapılmamışsa null. */
  lastRefundedAt: string | null;
  /** Bu sipariş için yapılmış iade hareketleri (timeline, eski → yeni). */
  refunds: RefundInfo[];
  /** Sadece dashboard `GET /orders/{orderNumber}` cevabında dolu; public sayfada null. */
  payment?: OrderPaymentInfo | null;
  /** Satış faturası kesildi — alıcı sipariş sayfasından PDF'i indirebilir. */
  invoiceAvailable: boolean;
}

export interface OrderPaymentInfo {
  /** `PaymentProviderKey` string'i; ödeme yöntemi seçilmediyse null. */
  provider: string | null;
  /** Provider tarafındaki işlem numarası (Param: Dekont_ID). */
  transactionId: string | null;
  installmentCount: number;
  installmentFeeTotal: number;
}

// ---------- Refund ----------

/**
 * Backend RefundReason enum mirror'ı. UI'da TR label'a `REFUND_REASON_LABELS`
 * map'i ile çevrilir. "Other" seçilirse `reasonNote` zorunlu.
 */
export type RefundReason =
  | "CustomerRequest"
  | "EventCancelled"
  | "OrderError"
  | "Other";

export const REFUND_REASON_LABELS: Record<RefundReason, string> = {
  CustomerRequest: "Müşteri talebi",
  EventCancelled: "Etkinlik iptal",
  OrderError: "Hatalı sipariş",
  Other: "Diğer",
};

/**
 * Bir iade hareketi — order detail sayfasında timeline kartında
 * gösterilir. Aynı order'da birden fazla refund olabilir (per-bilet kısmi).
 */
export interface RefundInfo {
  id: string;
  /** Backend RefundReason enum string. UI map'ine bakın. */
  reason: RefundReason;
  /** "Other" için zorunlu serbest metin; diğerlerinde opsiyonel. */
  reasonNote: string | null;
  /** Bu iadenin tutarı (positive). */
  amount: number;
  /** true → bu refund kısmiydi veya order zaten kısmen iade edilmişti. */
  isPartial: boolean;
  /** Bu refund'da kapsanan ticket id'leri. */
  refundedTicketIds: string[];
  refundedTicketCount: number;
  /** Snapshot — refund anındaki provider key. */
  paymentProvider: string | null;
  /** Provider tarafındaki iade transaction id'si (varsa). */
  providerRefundId: string | null;
  /** İadeyi başlatan member id (silinirse null'a düşer). */
  actorMemberId: string | null;
  /** Member email snapshot — silinse bile audit kaybolmasın. */
  actorEmail: string | null;
  emailSent: boolean;
  smsSent: boolean;
  createdAt: string;
}

/**
 * POST /orders/{orderNumber}/refund body.
 *
 * - `ticketIds` null/boş → tüm aktif (Valid) biletler iade edilir (full refund).
 * - Doluysa → seçili bilet(ler) iade. Kalan biletler aktif kalır.
 * - `reason` "Other" ise `reasonNote` zorunlu.
 * - `sendEmail`/`sendSms` modal'daki toggle'lardan gelir; default true.
 */
export interface RefundOrderRequest {
  reason: RefundReason;
  reasonNote?: string | null;
  ticketIds?: string[] | null;
  sendEmail: boolean;
  sendSms: boolean;
}

// ---------- Payments ----------

/**
 * Backend PaymentProviderKey enum (concrete provider). UI tipik olarak
 * doğrudan kullanmaz; method-level seçim yapılır. Sadece organizer
 * dashboard'unda "method altında hangi sağlayıcılar var" listesi için.
 */
export type PaymentProviderKey =
  | "Param"
  | "Isbank"
  | "Halkbank"
  | "Tami"
  | "Garanti"
  | "Wallet"
  | "Dev";

/**
 * Backend PaymentMethodKind enum. Kullanıcıya gösterilen ödeme yöntemi
 * kategorisi. Pay sayfası ve organizer dashboard bu kind'lar üzerinden çalışır.
 */
export type PaymentMethodKind = "CreditCard" | "Wallet" | "Mock";

export interface PaymentMethodProvider {
  key: PaymentProviderKey;
  displayName: string;
}

/**
 * Bir ödeme yönteminin shop-bazlı ve platform-bazlı durumu.
 *
 * - `isActive`: Organizer bu shop için method'u aktif etmiş mi?
 * - `isAvailable`: Platform tarafında method kullanıma açık mı? (Wallet=false şu an)
 * Public storefront yalnız `isActive && isAvailable` olanları görür.
 * Organizer dashboard hepsini görür ama Wallet aktive edilemez (yakında).
 */
export interface PaymentMethod {
  kind: PaymentMethodKind;
  displayName: string;
  description: string;
  isActive: boolean;
  isAvailable: boolean;
  providers: PaymentMethodProvider[];
}

export interface PaymentMethodListResponse {
  methods: PaymentMethod[];
}

export interface TogglePaymentMethodRequest {
  isActive: boolean;
}

/**
 * Storefront kart formundan toplanan veri. ASLA log'lanmamalı, localStorage'a
 * yazılmamalı. Bir kez form submit'inde backend'e gidip bellekten silinir.
 */
export interface CardInfoRequest {
  holderName: string;
  /** Sadece rakamlar — boşluklar client tarafında temizlenebilir, backend de temizliyor. */
  number: string;
  /** "MM" — 1-12, backend 2 haneye pad ediyor. */
  expMonth: string;
  /** "YY" veya "YYYY" — backend 4 haneye normalize ediyor. */
  expYear: string;
  cvc: string;
}

/**
 * Pay sayfası → backend init isteği. `methodKind` kullanıcının seçtiği
 * yöntem; backend altındaki concrete provider'ı kendi seçer.
 *
 * `card`: yalnız `CreditCard` method'unda zorunlu. Diğer method'larda
 * (`Mock`, `Wallet`) opsiyonel/null bırakılabilir.
 */
export interface InitPaymentRequest {
  methodKind: PaymentMethodKind;
  /** 1 = tek çekim. MVP'de her zaman 1. */
  installmentCount: number;
  card?: CardInfoRequest | null;
}

export interface InitPaymentResponse {
  orderNumber: string;
  /** 3D Secure hosted sayfanın URL'i. Browser'ı buraya yönlendir. */
  redirectUrl: string;
  /** Alıcının fiilen ödeyeceği tutar (taksit farkı dahil). */
  buyerTotal: number;
  currency: string;
}

// ---------- Cart (storefront sepet + reservation) ----------

/**
 * Backend `CartStatus` enum mirror'ı. Storefront yalnız `Active` cart'ı görür;
 * diğerleri historik/server-side state'ler.
 *
 * - `Active`: Tıklanabilir/düzenlenebilir; süre dolduğunda reaper Expired'a alır.
 * - `Converted`: Bağlı order Paid'e geçti — sepet artık historik.
 * - `Expired`: Süre doldu, Reserved iade edildi.
 * - `Abandoned`: Kullanıcı manuel boşalttı.
 */
export type CartStatusValue = "Active" | "Converted" | "Expired" | "Abandoned";

export interface CartItemResponse {
  id: string;
  ticketTypeId: string;
  ticketTypeName: string;
  quantity: number;
  /** Per-unit fiyat (ticket type catalog snapshot — BuildResponse anında okunur). */
  unitPrice: number;
  /** unitPrice * quantity. Backend'de hesaplanır, frontend trust eder. */
  lineTotal: number;
  currency: CurrencyCode;
  /**
   * Bu ticket type için "şu an satılabilir" stok = Quantity - Reserved - Sold.
   * Bu kullanıcının kendi reserved'ı buna dahil DEĞİL — yani availableNow,
   * "kullanıcı bu kadar daha ekleyebilir" demek değil, "tüm sistemde daha şu kadar var".
   */
  availableNow: number;
}

/**
 * Backend `CartResponse` — storefront sepet snapshot'ı.
 *
 * Stok rezervasyonu sunucuda; cart Active ve süresi içinde olduğu sürece bu items
 * stoktan düşülmüş kabul edilir. ExpiresAt geçince reaper iade eder.
 *
 * `serverTime` drift düzeltme için: client `Date.now()` ile karşılaştırılarak
 * (serverTime - clientNow) offset hesaplanır, countdown bunu kullanır.
 */
export interface CartResponse {
  id: string;
  status: CartStatusValue;
  shopId: string;
  eventId: string;
  eventSlug: string;
  shopSlug: string;
  /** ISO UTC. Active cart'ta geçerli; reaper veya success/cancel anında effectively dondurulur. */
  expiresAt: string;
  createdAt: string;
  /** ISO UTC sunucu saati — clock drift düzeltmesi için. */
  serverTime: string;
  /** Cart'ın baskın currency'si (ilk item) — şu an hep tek currency. */
  currency: CurrencyCode;
  /** Tüm satırların lineTotal toplamı (KDV/servis hariç ham toplam). */
  subtotal: number;
  totalQuantity: number;
  items: CartItemResponse[];
}

/** POST /public/carts/items request. */
export interface AddCartItemRequest {
  shopSlug: string;
  eventSlug: string;
  ticketTypeId: string;
  /** 1-50 arası. Backend'de aynı şekilde validate edilir. */
  quantity: number;
}

/** PATCH /public/carts/items/{ticketTypeId} request. quantity=0 → satır silinir. */
export interface UpdateCartItemRequest {
  quantity: number;
}

// ---------- Checkout (single-page) ----------

/**
 * `POST /public/checkout` outcome — server bir sonraki adımı belirtir.
 *
 * - `Redirect`: 3DS hosted payment sayfasına yönlendir (`redirectUrl`).
 * - `Html`: Provider HTML/POST formu döndü; document.write ile bas.
 * - `Success`: Ödeme adımı olmadan Paid (toplam 0). `successUrl` shop-göreli sipariş
 *   sayfası; embed'de `/embed/...?t=` erişim anahtarıyla.
 */
export type CheckoutOutcomeValue = "Redirect" | "Html" | "Success";

/**
 * Storefront alıcı bilgisi. Misafir checkout — auth yok.
 *
 * `tcKimlik`: brüt total >= 9900 TL ise zorunlu (backend zorlar). Aksi halde null.
 */
export interface CheckoutBuyerInfo {
  firstName: string;
  lastName: string;
  email: string;
  /** E.164 formatında telefon. */
  phone: string;
  tcKimlik?: string | null;
}

/**
 * Ödeme seçimi. `kind` discriminator'a göre alanlar değişir:
 * - `CreditCard`: `card` zorunlu, `installmentCount >= 1`.
 * - `Mock`: dev provider — kart bilgisi yok, success/fail simülasyonu.
 * - `Wallet`: `walletProvider` zorunlu (ileride; MVP'de devre dışı).
 */
export interface CheckoutPaymentSelection {
  kind: PaymentMethodKind;
  card?: CardInfoRequest | null;
  installmentCount?: number;
  walletProvider?: PaymentProviderKey | null;
}

export interface CheckoutRequest {
  cartId: string;
  buyer: CheckoutBuyerInfo;
  /** Toplamı 0 olan siparişte null; backend sağlayıcıya gitmeden onaylar. */
  payment: CheckoutPaymentSelection | null;
  /** Opsiyonel kupon kodu — backend `?code=` ile aynı yere düşer. */
  couponCode?: string | null;
  /**
   * Embed iframe akışında organizer'ın site URL'i. Backend bu URL'in host'unu
   * shop.AllowedEmbedDomains whitelist'inden geçirir; geçmezse sessizce null'a
   * düşer (sipariş yine başarılı olur, sadece "Siteye Dön" CTA'sı görünmez).
   */
  embedReturnUrl?: string | null;
  /** Null ise bireysel fatura. Vergi no checksum'ı backend'de doğrulanır. */
  corporateInvoice?: CorporateInvoiceRequest | null;
  /**
   * `Embed`: checkout organizatör sitesindeki iframe içinde; banka dönüşü
   * iframe'deki sonuç sayfasına yönlenir. Default `Shop`.
   */
  channel?: OrderChannel;
  /** Yalnız shop'ta, alıcı mağazanın çerez bandında Kabul dediyse (Meta CAPI). */
  marketing?: CheckoutMarketing | null;
}

/** `_fbp` / `_fbc` Meta Pixel'in kendi çerezleri; yoksa null. */
export interface CheckoutMarketing {
  consent: boolean;
  fbp: string | null;
  fbc: string | null;
}

export type OrderChannel = "Shop" | "Embed";

export interface CorporateInvoiceRequest {
  companyName: string;
  /** 10 haneli VKN veya şahıs şirketi için 11 haneli TCKN. */
  taxNumber: string;
  taxOffice: string;
  address: string;
  city: string;
  district: string;
}

// ---------------- Invoicing (dashboard) ----------------

export type InvoiceKind = "Sale" | "Refund";

export type InvoiceStatus =
  | "Pending"
  | "ContactCreated"
  | "InvoiceCreated"
  | "Paid"
  | "DocumentQueued"
  | "DocumentIssued"
  | "Completed"
  | "Skipped"
  | "Failed";

export type InvoiceDocumentType = "EArchive" | "EInvoice";

export interface InvoiceSummary {
  id: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  documentType: InvoiceDocumentType | null;
  grossTotal: number;
  hasPdf: boolean;
  createdAt: string;
  completedAt: string | null;
}

export interface BillingInfo {
  isCorporate: boolean;
  /** İlk 3 ve son 2 hane görünür. */
  tcKimlikMasked: string | null;
  companyName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  address: string | null;
  city: string | null;
  district: string | null;
}

export interface OrderInvoicingResponse {
  /** Bu ortamda faturalama işi açık mı (`Invoicing:Enabled`). */
  enabled: boolean;
  billing: BillingInfo;
  invoices: InvoiceSummary[];
}

/**
 * `POST /public/checkout` response. `outcome`'a göre frontend yönlendirir:
 * - Redirect → window.location.href = redirectUrl
 * - Html → document.open + write(htmlContent)
 * - Success → router.push(successUrl)
 */
export interface CheckoutResponse {
  orderNumber: string;
  outcome: CheckoutOutcomeValue;
  redirectUrl?: string | null;
  htmlContent?: string | null;
  successUrl?: string | null;
  /** Alıcının fiilen ödeyeceği tutar (taksit farkı dahil). */
  buyerTotal: number;
  currency: string;
  /**
   * Embed siparişinde banka sayfası iframe'de açılamıyorsa true — banka adımı
   * üst pencerede açılır.
   */
  openInTopWindow: boolean;
}

// ---------- Promotions ----------

export type PromotionTypeValue = "BuyXGetY" | "Percentage" | "FixedAmount";

export interface PromotionTicketTypeSummary {
  id: string;
  name: string;
}

export interface Promotion {
  id: string;
  name: string;
  description: string | null;
  /** Opsiyonel kupon kodu (organizer-scope unique). Boş → otomatik. */
  code: string | null;
  type: PromotionTypeValue;
  /** BuyXGetY: ücretli adet eşiği. */
  buyX: number | null;
  /** BuyXGetY: bedava eklenen adet. */
  getY: number | null;
  /** Percentage: 0-100, FixedAmount: tutar. */
  discountValue: number | null;
  /** FixedAmount currency. */
  discountCurrency: CurrencyCode | null;
  startsAt: string | null;
  endsAt: string | null;
  /** null → organizatörün tüm etkinlikleri. */
  eventId: string | null;
  eventTitle: string | null;
  /** Boş → event'in tüm ticket türleri. */
  ticketTypes: PromotionTicketTypeSummary[];
  maxUses: number | null;
  usageCount: number;
  priority: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  stats: PromotionStats;
}

/**
 * Ödenmiş (Paid / PartiallyRefunded) ve geri alınmamış kullanımlar üzerinden.
 * `usageCount`'tan farklı olarak ödeme bekleyen siparişleri saymaz.
 */
export interface PromotionStats {
  paidOrderCount: number;
  freeTicketTotal: number;
  /** Para birimi başına; ciroya göre büyükten küçüğe. */
  totals: PromotionCurrencyTotal[];
}

export interface PromotionCurrencyTotal {
  currency: string;
  discountTotal: number;
  /** Promosyonlu siparişlerin tutarı, kısmi iadeler düşülmüş. */
  revenueTotal: number;
}

export interface CreatePromotionRequest {
  name: string;
  description?: string | null;
  code?: string | null;
  type: PromotionTypeValue;
  buyX?: number | null;
  getY?: number | null;
  discountValue?: number | null;
  discountCurrency?: CurrencyCode | null;
  startsAt?: string | null;
  endsAt?: string | null;
  eventId?: string | null;
  ticketTypeIds?: string[] | null;
  maxUses?: number | null;
  priority: number;
  isActive: boolean;
}

export type UpdatePromotionRequest = CreatePromotionRequest;

export interface PromotionRedemption {
  id: string;
  promotionId: string;
  orderId: string;
  orderNumber: string;
  codeUsed: string | null;
  discountAmount: number;
  freeQuantity: number;
  redeemedAt: string;
  isReversed: boolean;
  reversedAt: string | null;
}

/**
 * Organizer dashboard'da satışları listeleme.
 */
export interface OrganizerOrderListItem {
  orderNumber: string;
  status: OrderStatus;
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerPhone: string;
  total: number;
  currency: string;
  ticketCount: number;
  eventTitle: string;
  eventSlug: string;
  shopSlug: string;
  createdAt: string;
  paidAt: string | null;
}

/**
 * GET /orders response — liste + sayfalama meta bilgisi.
 * `hasMore` bir sonraki sayfanın var olup olmadığını belirtir ("Daha fazla yükle").
 */
export interface OrganizerOrderListResponse {
  items: OrganizerOrderListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
}

/** Dashboard orders list query parametreleri. */
export interface OrganizerOrderListQuery {
  search?: string;
  status?: OrderStatus;
  page?: number;
  pageSize?: number;
}

export interface CancelOrderRequest {
  reason?: string;
}

/**
 * Backend'den gelen RFC 7807 ProblemDetails formatı.
 *
 * `traceId`: backend hata yanıtına isteğin W3C iz kimliğini (32 hex; yoksa request id'yi)
 * extension olarak koyar. UI 5xx hatalarda kullanıcıya copy butonlu olarak gösterir; destek
 * talebinde bu kod Cloud Logging'de log satırlarının trace alanıyla eşleşir.
 *
 * `errors`: ValidationException multi-field breakdown'da set edilir (form alanı → mesaj listesi).
 */
export interface ApiProblem {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  traceId?: string;
}

/* ============================================================================
 * i18n helpers — lokalize formatting utilities.
 * ========================================================================= */

/**
 * Intl.NumberFormat ile currency formatla. `amount` major unit (örn. 125.50 TRY).
 *
 * TR formatlaması: "125,50 ₺". EN: "$125.50". AR: RTL-aware.
 */
export function formatCurrency(
  amount: number,
  currency: CurrencyCode,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return new Intl.NumberFormat(localeToBcp47(locale), {
    style: "currency",
    currency,
    // Tam sayılar "125 ₺" olarak çıksın, kuruşlu "125,50 ₺"
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Lokalize tarih/saat formatlama. Default Istanbul timezone'unda basılır;
 * ileride shop.timezone eklenirse oradan alınır.
 */
export function formatDateTime(
  value: string | Date,
  locale: Locale = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  },
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat(localeToBcp47(locale), {
    timeZone: "Europe/Istanbul",
    ...options,
  }).format(date);
}

/** "tr" → "tr-TR", "en" → "en-US", "ar" → "ar". Intl API'lar region-aware. */
function localeToBcp47(locale: Locale): string {
  switch (locale) {
    case "tr":
      return "tr-TR";
    case "en":
      return "en-US";
    case "ar":
      return "ar-u-nu-latn";
  }
}

/* ============================================================================
 * Pricing helpers — backend `PricingCalculator` ile simetrik. Storefront'ta
 * ön-izleme / breakdown için kullanılır (dashboard form preview dahil).
 * Aritmetik aynı: round-half-to-even, 2 ondalık.
 * ========================================================================= */

/** 2 basamağa round, banker's rounding (round-half-to-even). */
export function roundMoney(value: number): number {
  const scaled = value * 100;
  const floor = Math.floor(scaled);
  const diff = scaled - floor;
  let rounded: number;
  if (diff > 0.5) rounded = floor + 1;
  else if (diff < 0.5) rounded = floor;
  else rounded = floor % 2 === 0 ? floor : floor + 1;
  return rounded / 100;
}

export interface PriceBreakdown {
  basePrice: number;
  taxAmount: number;
  serviceFee: number;
  commission: number;
  unitGross: number;
  lineGross: number;
  lineBase: number;
  lineTax: number;
  lineServiceFee: number;
  lineCommission: number;
}

/**
 * Bilet başı servis bedeli — liste fiyatı 0 olan (ücretsiz) bilette alınmaz.
 * Backend `PricingCalculator.ServiceFeeFor` ile aynı kural.
 */
export function serviceFeeFor(basePrice: number, organizerServiceFee: number): number {
  return basePrice > 0 ? organizerServiceFee : 0;
}

/**
 * Per-unit brüt fiyat — alıcının ödediği rakam.
 *   gross = base + (base * taxRate/100) + serviceFee
 * Storefront ticket card / detay / event listesinde bu gösterilir.
 */
export function calculateGross(
  basePrice: number,
  taxRatePercent: number,
  serviceFee: number,
): number {
  const tax = roundMoney((basePrice * taxRatePercent) / 100);
  return roundMoney(basePrice + tax + serviceFeeFor(basePrice, serviceFee));
}

/**
 * Dashboard ticket form'unda canlı fiyat ön-izleme için tam breakdown.
 * Organizer "base 100, KDV 20%, fee 5" girdiğinde "Alıcı öder: 125" göstermek için.
 */
export function calculatePriceBreakdown(
  basePrice: number,
  taxRatePercent: number,
  serviceFee: number,
  commissionRatePercent: number,
  quantity: number = 1,
): PriceBreakdown {
  serviceFee = serviceFeeFor(basePrice, serviceFee);
  const taxAmount = roundMoney((basePrice * taxRatePercent) / 100);
  const commission = roundMoney((basePrice * commissionRatePercent) / 100);
  const unitGross = roundMoney(basePrice + taxAmount + serviceFee);
  return {
    basePrice,
    taxAmount,
    serviceFee,
    commission,
    unitGross,
    lineGross: roundMoney(unitGross * quantity),
    lineBase: roundMoney(basePrice * quantity),
    lineTax: roundMoney(taxAmount * quantity),
    lineServiceFee: roundMoney(serviceFee * quantity),
    lineCommission: roundMoney(commission * quantity),
  };
}

/* ============================================================================
 * Reports — Dashboard ana ekran response.
 * Backend: TicketNjoy.Api.Features.Reports.DashboardResponse mirror.
 * ========================================================================= */

/**
 * Ana sayfa ve satış raporu aralığı — backend `ReportPeriod` ile senkron, İstanbul
 * takvimi. `custom`'da `from`/`to` İstanbul günü (`yyyy-MM-dd`, iki uç dahil).
 */
export type ReportRange = "today" | "yesterday" | "week" | "month" | "custom";

export type DashboardTrendBucket = "hour" | "day";

export interface DashboardKpis {
  revenue: number;
  organizerNet: number;
  platformRevenue: number;
  ticketsSold: number;
  ordersPaid: number;
  ordersPending: number;
  ordersRefunded: number;
  averageOrderValue: number;
}

export interface DashboardTrendPoint {
  /** ISO; gün kovasında İstanbul gece yarısının UTC karşılığı. */
  bucketUtc: string;
  revenue: number;
  ticketsSold: number;
}

export interface DashboardTopEvent {
  eventId: string;
  eventSlug: string;
  eventTitle: string;
  coverImageUrl: string | null;
  startsAt: string;
  ticketsSold: number;
  revenue: number;
}

export interface DashboardOccupancyItem {
  eventId: string;
  eventSlug: string;
  eventTitle: string;
  startsAt: string;
  totalCapacity: number;
  sold: number;
  reserved: number;
  occupancyRate: number; // 0..1
}

export interface DashboardRefundAnalysis {
  count: number;
  total: number;
  refundRate: number; // 0..1
}

export interface DashboardPaymentBreakdownItem {
  provider: string;
  orderCount: number;
  revenue: number;
}

export interface DashboardRecentOrder {
  orderNumber: string;
  status: OrderStatus;
  buyerEmail: string;
  buyerName: string;
  total: number;
  createdAt: string;
  eventTitle: string;
}

export interface DashboardCustomerStats {
  uniqueCustomers: number;
  repeatCustomers: number;
  lifetimeValue: number;
}

export interface DashboardResponse {
  range: ReportRange;
  fromUtc: string;
  toUtc: string;
  trendBucket: DashboardTrendBucket;
  currency: CurrencyCode;
  kpis: DashboardKpis;
  trend: DashboardTrendPoint[];
  topEvents: DashboardTopEvent[];
  occupancy: DashboardOccupancyItem[];
  refunds: DashboardRefundAnalysis;
  paymentMethods: DashboardPaymentBreakdownItem[];
  recentOrders: DashboardRecentOrder[];
  customers: DashboardCustomerStats;
}

/* ============================================================================
 * Audit Log — backend Features/Audit ile senkron.
 * Yeni audit action eklerken hem `AUDIT_ACTIONS` hem `AUDIT_ACTION_LABELS`
 * güncellenir; backend `AuditActions` registry ile bire bir mirror.
 * ========================================================================= */

/** Backend `AuditEntityTypes` mirror'ı. */
export const AUDIT_ENTITY_TYPES = [
  "Member",
  "Event",
  "Order",
  "PaymentMethod",
  "EventReport",
  "Organizer",
  "Payout",
  "Shop",
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

/** Entity tipi TR label'ı — filtre dropdown'ı + tablo "ne" sütunu. */
export const AUDIT_ENTITY_TYPE_LABELS: Record<AuditEntityType, string> = {
  Member: "Üye",
  Event: "Etkinlik",
  Order: "Sipariş",
  PaymentMethod: "Ödeme yöntemi",
  EventReport: "Etkinlik raporu",
  Organizer: "Hesap",
  Payout: "Ödeme",
  Shop: "Mağaza",
};

/** Backend `AuditActions` const string registry mirror. */
export const AUDIT_ACTIONS = [
  "Member.Invite",
  "Member.RoleChange",
  "Member.StatusChange",
  "Member.Delete",
  "Member.PasswordReset",
  "Member.PasswordChange",
  "Member.PasswordResetLink",
  "Member.InviteResend",
  "Member.EmailVerify",
  "Member.SessionsRevoke",
  "Member.EmailChange",
  "Member.OwnerTransfer",
  "Event.Publish",
  "Event.Pause",
  "Event.Resume",
  "Event.Cancel",
  "Order.Refund",
  "Order.Cancel",
  "Order.BuyerUpdate",
  "Order.NotificationResend",
  "Order.PaymentConfirm",
  "Order.PaymentRefundWithoutTickets",
  "PaymentMethod.Toggle",
  "EventReport.Regenerate",
  "Organizer.FeeChange",
  "Organizer.Suspend",
  "Organizer.Unsuspend",
  "Organizer.BankAccountChange",
  "Organizer.ImpersonationStart",
  "Organizer.ImpersonationEnd",
  "Organizer.ProfileUpdate",
  "Organizer.LegalInfoUpdate",
  "Organizer.PaymentSettingsChange",
  "Payout.Request",
  "Payout.Cancel",
  "Payout.Create",
  "Payout.Update",
  "Payout.Approve",
  "Payout.MarkPaid",
  "Payout.Reject",
  "Payout.Adjustment",
  "Shop.TrackingChange",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Action TR label — tablo "yapılan iş" sütunu. */
export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  "Member.Invite": "Üye davet edildi",
  "Member.RoleChange": "Üye rolü değişti",
  "Member.StatusChange": "Üye durumu değişti",
  "Member.Delete": "Üye silindi",
  "Member.PasswordReset": "Şifre sıfırlandı",
  "Member.PasswordChange": "Şifre değiştirildi",
  "Member.PasswordResetLink": "Şifre sıfırlama linki gönderildi",
  "Member.InviteResend": "Davet tekrar gönderildi",
  "Member.EmailVerify": "E-posta doğrulanmış sayıldı",
  "Member.SessionsRevoke": "Üyenin oturumları kapatıldı",
  "Member.EmailChange": "Üye e-postası değişti",
  "Member.OwnerTransfer": "Hesap sahipliği devredildi",
  "Event.Publish": "Etkinlik yayınlandı",
  "Event.Pause": "Etkinlik duraklatıldı",
  "Event.Resume": "Etkinlik tekrar yayında",
  "Event.Cancel": "Etkinlik iptal edildi",
  "Order.Refund": "Sipariş iadesi",
  "Order.Cancel": "Sipariş iptali",
  "Order.BuyerUpdate": "Alıcı bilgisi düzeltildi",
  "Order.NotificationResend": "Bilet bildirimi tekrar gönderildi",
  "Order.PaymentConfirm": "Ödeme TicketNjoy tarafından onaylandı",
  "Order.PaymentRefundWithoutTickets": "Ödeme biletsiz iade edildi",
  "PaymentMethod.Toggle": "Ödeme yöntemi değişti",
  "EventReport.Regenerate": "Etkinlik raporu yeniden oluşturuldu",
  "Organizer.FeeChange": "Komisyon / servis ücreti değişti",
  "Organizer.Suspend": "Hesap askıya alındı",
  "Organizer.Unsuspend": "Hesap tekrar açıldı",
  "Organizer.BankAccountChange": "Banka hesabı değişti",
  "Organizer.ImpersonationStart": "TicketNjoy destek oturumu açıldı",
  "Organizer.ImpersonationEnd": "TicketNjoy destek oturumu kapandı",
  "Organizer.ProfileUpdate": "Hesap profili düzenlendi",
  "Organizer.LegalInfoUpdate": "Fatura bilgileri düzenlendi",
  "Organizer.PaymentSettingsChange": "Kart komisyonu değişti",
  "Payout.Request": "Ödeme talep edildi",
  "Payout.Cancel": "Ödeme talebi iptal edildi",
  "Payout.Create": "Ödeme oluşturuldu",
  "Payout.Update": "Ödeme düzenlendi",
  "Payout.Approve": "Ödeme onaylandı",
  "Payout.MarkPaid": "Ödeme gönderildi",
  "Payout.Reject": "Ödeme reddedildi",
  "Payout.Adjustment": "Bakiye düzeltmesi",
  "Shop.TrackingChange": "İzleme kimlikleri değişti",
};

/* ============================================================================
 * Payouts — backend Features/Payouts ile senkron. Dashboard Finans sayfası ve
 * admin Cari kartı aynı `PayoutSummary` yanıtını kullanır.
 * ========================================================================= */

export type PayoutStatus = "Requested" | "Approved" | "Paid" | "Rejected" | "Cancelled";
export type PayoutSource = "Organizer" | "Admin";

export const PAYOUT_STATUS_LABELS: Record<PayoutStatus, string> = {
  Requested: "Onay bekliyor",
  Approved: "Onaylandı",
  Paid: "Ödendi",
  Rejected: "Reddedildi",
  Cancelled: "İptal edildi",
};

export interface PayoutBankAccount {
  iban: string | null;
  ibanOwnerName: string | null;
}

/**
 * Para birimi başına bakiye.
 * `available = settledEarnings + adjustments − paidOut − inProgress`; avansta eksi olabilir.
 */
export interface PayoutBalance {
  currency: CurrencyCode;
  /** Henüz bitmemiş etkinliklerin neti — çekilemez. */
  pendingEarnings: number;
  /** Biten etkinliklerin neti. */
  settledEarnings: number;
  adjustments: number;
  paidOut: number;
  /** Talep edilmiş / onaylanmış, henüz ödenmemiş. */
  inProgress: number;
  available: number;
}

export interface PayoutEventEarning {
  eventId: string;
  title: string;
  endsAt: string;
  currency: CurrencyCode;
  orderCount: number;
  /** İade oranında ölçeklenmiş organizatör payı (etkinlik sonu raporundaki net). */
  net: number;
  settled: boolean;
}

export interface Payout {
  id: string;
  currency: CurrencyCode;
  amount: number;
  status: PayoutStatus;
  source: PayoutSource;
  iban: string;
  ibanOwnerName: string;
  note: string | null;
  rejectReason: string | null;
  bankReference: string | null;
  paidAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export interface BalanceAdjustment {
  id: string;
  currency: CurrencyCode;
  amount: number;
  reason: string;
  createdAt: string;
}

export interface PayoutSummary {
  bankAccount: PayoutBankAccount;
  balances: PayoutBalance[];
  events: PayoutEventEarning[];
  payouts: Payout[];
  adjustments: BalanceAdjustment[];
}

export interface UpdateBankAccountRequest {
  iban: string;
  ibanOwnerName: string;
}

export interface CreatePayoutRequest {
  currency: CurrencyCode;
  amount: number;
}

/** "TR33 0006 1005 1978 6457 8413 26" — giriş ve gösterimde 4'lü gruplar. */
export function formatIban(iban: string | null | undefined): string {
  if (!iban) return "";
  return iban.replace(/\s+/g, "").toUpperCase().replace(/(.{4})/g, "$1 ").trim();
}

/**
 * Bir audit log satırı. `diff` jsonb string olarak gelir (backend serialize etti);
 * frontend `JSON.parse` edip pretty-print eder. Convention:
 * <code>{"field": {"oldValue": ..., "newValue": ...}}</code>.
 */
export interface AuditLogEntry {
  id: string;
  actorMemberId: string | null;
  actorEmail: string | null;
  entityType: AuditEntityType | string;
  entityId: string;
  action: AuditAction | string;
  /** JSON string. Null olabilir; UI'da "—" gösterilir. */
  diff: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

/** GET /audit response — sayfalı liste. */
export interface AuditLogListResponse {
  items: AuditLogEntry[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
}

/**
 * Audit list filtre parametreleri. Tümü opsiyonel — boş ise tüm kayıtlar
 * CreatedAt desc sıralı döner.
 */
export interface AuditLogQuery {
  /** ISO UTC, inclusive (>=). */
  from?: string | null;
  /** ISO UTC, exclusive (<). */
  to?: string | null;
  /** Bir member'ın aksiyonlarını filtrele. */
  actorMemberId?: string | null;
  entityType?: AuditEntityType | null;
  action?: AuditAction | null;
  page?: number;
  pageSize?: number;
}

/* ============================================================================
 * Sales Reports — Faz 1 P4. Backend Features/Reports/Sales* mirror.
 * Refund handling: PartiallyRefunded dahil ama aktif kısım hesaplanır,
 * Refunded (full) hariç (bkz. SalesHandler XML doc).
 * ========================================================================= */

/** Backend SalesGroupBy enum mirror (string serialize). */
export type SalesGroupBy = "Day" | "Event" | "TicketType" | "PaymentMethod";

/** UI label'ları + ikon yardımcıları için (sidebar select). */
export const SALES_GROUP_BY_OPTIONS: Array<{
  value: SalesGroupBy;
  label: string;
}> = [
  { value: "Day", label: "Günlük" },
  { value: "Event", label: "Etkinlik" },
  { value: "TicketType", label: "Bilet Türü" },
  { value: "PaymentMethod", label: "Ödeme Yöntemi" },
];

export interface SalesReportRow {
  /** Grup etiketi (gün ISO / event title / ticket type adı / ödeme yöntemi). */
  key: string;
  /** Sıralama için stable identifier (ISO tarih, eventId, ticketTypeId). */
  sortKey: string;
  /** TicketType groupBy'da 0 (bilet bazlı). */
  orderCount: number;
  /** İade edilmemiş aktif bilet sayısı. */
  ticketCount: number;
  /** Alıcının ödediği = subtotal + tax + serviceFee (refund-aware). */
  grossRevenue: number;
  taxTotal: number;
  serviceFeeTotal: number;
  commissionTotal: number;
  /** Organizer'a giden net (komisyon ve kart fee düşülmüş). */
  organizerNetRevenue: number;
}

export interface SalesReportTotals {
  orderCount: number;
  ticketCount: number;
  grossRevenue: number;
  taxTotal: number;
  serviceFeeTotal: number;
  commissionTotal: number;
  organizerNetRevenue: number;
}

export interface SalesReportResponse {
  fromUtc: string;
  toUtc: string;
  groupBy: SalesGroupBy;
  currency: CurrencyCode;
  rows: SalesReportRow[];
  totals: SalesReportTotals;
}

export interface SalesReportQuery {
  range: ReportRange;
  /** `yyyy-MM-dd`, yalnız `range: "custom"` iken gönderilir. */
  from?: string | null;
  to?: string | null;
  shopId?: string | null;
  eventId?: string | null;
  ticketTypeId?: string | null;
  groupBy: SalesGroupBy;
}

// ============================================================================
// Event Report Snapshot (P5) — gece bg job ile üretilen immutable rapor
// ============================================================================

/** Liste sayfası satırı — payload'sız özet (KPI hızlı render için). */
export interface EventReportListItem {
  snapshotId: string;
  eventId: string;
  shopId: string;
  eventTitle: string;
  /** ISO UTC. */
  eventEndsAt: string;
  /** ISO UTC — snapshot ne zaman üretildi. */
  snapshotTakenAt: string;
  orderCount: number;
  ticketsSold: number;
  ticketsCheckedIn: number;
  grossRevenue: number;
  refundedAmount: number;
  organizerNetRevenue: number;
}

export interface EventReportListResponse {
  items: EventReportListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** Üst seviye KPI özeti — detay hero. */
export interface EventReportSummary {
  orderCount: number;
  ticketsSold: number;
  ticketsCheckedIn: number;
  ticketsNoShow: number;
  /** 0..1 — UI yüzdeye çevirir. */
  attendanceRate: number;
  grossRevenue: number;
  taxTotal: number;
  serviceFeeTotal: number;
  commissionTotal: number;
  organizerNetRevenue: number;
  refundedAmount: number;
  averageOrderValue: number;
  averageTicketPrice: number;
}

export interface EventReportTicketTypeRow {
  ticketTypeId: string;
  name: string;
  sold: number;
  checkedIn: number;
  grossRevenue: number;
  commissionTotal: number;
  organizerNetRevenue: number;
}

export interface EventReportPaymentRow {
  provider: string;
  label: string;
  orderCount: number;
  grossRevenue: number;
}

export interface EventReportRefundReasonRow {
  reason: "CustomerRequest" | "EventCancelled" | "OrderError" | "Other";
  label: string;
  count: number;
  amount: number;
}

export interface EventReportRefundAnalysis {
  fullRefundCount: number;
  partialRefundCount: number;
  refundedTicketCount: number;
  totalRefundedAmount: number;
  byReason: EventReportRefundReasonRow[];
}

export interface EventReportPromotionRow {
  promotionId: string;
  code: string;
  name: string;
  redemptionCount: number;
  discountTotal: number;
  freeQuantityTotal: number;
}

export interface EventReportSalesTrendPoint {
  /** ISO yyyy-MM-dd. */
  date: string;
  orderCount: number;
  ticketCount: number;
  grossRevenue: number;
}

export interface EventReportCheckInBucket {
  /** ISO UTC, saat kovası başlangıcı. */
  hourUtc: string;
  count: number;
}

export interface EventReportCheckInMetrics {
  firstCheckedInAt: string | null;
  lastCheckedInAt: string | null;
  /** "20:00-21:00" gibi label. */
  peakHourLabel: string | null;
  peakHourCount: number;
  hourlyBuckets: EventReportCheckInBucket[];
}

export interface EventReportPayload {
  schemaVersion: number;
  currency: CurrencyCode;
  summary: EventReportSummary;
  ticketTypes: EventReportTicketTypeRow[];
  paymentBreakdown: EventReportPaymentRow[];
  refundAnalysis: EventReportRefundAnalysis;
  promotionUsage: EventReportPromotionRow[];
  salesTrend: EventReportSalesTrendPoint[];
  checkIn: EventReportCheckInMetrics;
}

export interface EventReportDetailResponse {
  snapshotId: string;
  eventId: string;
  shopId: string;
  organizerId: string;
  eventTitle: string;
  eventEndsAt: string;
  snapshotTakenAt: string;
  schemaVersion: number;
  payload: EventReportPayload;
}

/* ============================================================================
 * Platform admin — backend Features/Admin ile senkron. Sadece apps/admin kullanır.
 * ========================================================================= */

export type PlatformAdminRole = "SuperAdmin" | "Support";

export interface AdminMe {
  id: string;
  email: string;
  displayName: string;
  role: PlatformAdminRole;
  sessionExpiresAt: string;
}

/** Şifre doğru, kod e-postaya gitti. */
export interface AdminLoginChallenge {
  challengeToken: string;
  expiresAt: string;
  maskedEmail: string;
}

/**
 * POST /admin/auth/login. Kodu daha önce doğrulanmış IP'den gelindiyse oturum hemen açılır
 * (çerez yazıldı, `me` dolu); değilse `challenge` döner.
 */
export type AdminLoginResponse =
  | { codeRequired: true; challenge: AdminLoginChallenge; me: null }
  | { codeRequired: false; challenge: null; me: AdminMe };

/**
 * Satış raporuyla aynı kural: Paid + PartiallyRefunded, kısmi iade oranıyla
 * ölçeklenir. `platformRevenue` = komisyon + servis ücreti.
 */
export interface AdminCurrencyTotals {
  currency: CurrencyCode;
  paidOrderCount: number;
  netSales: number;
  refundedTotal: number;
  commission: number;
  serviceFee: number;
  platformRevenue: number;
}

export interface AdminOrganizerCounts {
  total: number;
  active: number;
  pendingEmailVerification: number;
  suspended: number;
  newLast30Days: number;
  sellingLast30Days: number;
}

export interface AdminPayoutCounts {
  /** Organizatör talep etti, onay bekliyor. */
  awaitingApproval: number;
  /** Onaylandı, EFT + "ödendi" işareti bekliyor. */
  awaitingTransfer: number;
}

export interface AdminOverview {
  organizers: AdminOrganizerCounts;
  payouts: AdminPayoutCounts;
  allTime: AdminCurrencyTotals[];
  last30Days: AdminCurrencyTotals[];
}

export interface AdminOrganizerListItem {
  id: string;
  companyName: string;
  email: string;
  status: OrganizerStatus;
  commissionRate: number;
  serviceFee: number;
  shopCount: number;
  lastSaleAt: string | null;
  createdAt: string;
}

export interface AdminOrganizerListResponse {
  items: AdminOrganizerListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminShopSummary {
  id: string;
  slug: string;
  name: string;
  status: ShopStatus;
  storefrontType: string;
}

/** Platform audit satırı. `diff` jsonb string, `AuditLogEntry` ile aynı convention. */
export interface AdminAuditEntry {
  id: string;
  action: string;
  actorEmail: string;
  diff: string | null;
  createdAt: string;
}

export interface AdminOrganizerDetail {
  id: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  status: OrganizerStatus;
  emailVerifiedAt: string | null;
  legalName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  billingAddress: string | null;
  commissionRate: number;
  serviceFee: number;
  suspendedAt: string | null;
  suspensionReason: string | null;
  createdAt: string;
  preferredLocale: string;
  logoUrl: string | null;
  memberCount: number;
  shops: AdminShopSummary[];
  sales: AdminCurrencyTotals[];
  recentActions: AdminAuditEntry[];
}

export interface AdminUpdateFeesRequest {
  commissionRate: number;
  serviceFee: number;
}

export interface AdminUpdateOrganizerContactRequest {
  phoneNumber: string;
  preferredLocale: string;
}

export interface AdminUpdateCompanyNameRequest {
  companyName: string;
}

export interface AdminPaymentProviderHealth {
  lastPaidAt: string | null;
  lastFailedAt: string | null;
  paidLast24h: number;
  failedLast24h: number;
  /** Sağlayıcının Sorunlu ödeme listesindeki siparişleri. */
  openIssueCount: number;
  lastCheckedAt: string | null;
  lastCheckOk: boolean | null;
  lastCheckMessage: string | null;
}

export interface AdminPaymentProviderItem {
  key: PaymentProviderKey;
  displayName: string;
  methodKind: PaymentMethodKind;
  isAvailable: boolean;
  isDefault: boolean;
  /** false = entegrasyon yok (Cüzdan); açılamaz. */
  ready: boolean;
  cardFeeRate: number;
  /** Bu POS'u açıkça atanmış organizatörler (varsayılandan gelenler hariç). */
  assignedOrganizerCount: number;
  health: AdminPaymentProviderHealth;
}

export interface AdminSetProviderAvailabilityRequest {
  isAvailable: boolean;
  reason?: string | null;
}

export interface AdminUpdateProviderCardFeeRequest {
  cardFeeRate: number;
}

export interface AdminCardProviderOption {
  key: PaymentProviderKey;
  displayName: string;
  isAvailable: boolean;
  isDefault: boolean;
  cardFeeRate: number;
}

export interface AdminOrganizerPaymentSettings {
  /** Atanmış POS; null = platform varsayılanı. */
  cardProvider: PaymentProviderKey | null;
  cardFeeRateOverride: number | null;
  /** Kartla ödemede şu an kullanılacak POS; açık kart POS'u yoksa null. */
  effectiveCardProvider: PaymentProviderKey | null;
  effectiveCardProviderName: string | null;
  effectiveCardFeeRate: number | null;
  cardProviders: AdminCardProviderOption[];
}

export interface AdminUpdateOrganizerPaymentSettingsRequest {
  cardProvider: PaymentProviderKey | null;
  cardFeeRateOverride: number | null;
}

export interface AdminPayoutListItem {
  id: string;
  organizerId: string;
  companyName: string;
  currency: CurrencyCode;
  amount: number;
  status: PayoutStatus;
  source: PayoutSource;
  createdAt: string;
  paidAt: string | null;
}

export interface AdminPayoutListResponse {
  items: AdminPayoutListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** `note` tutar çekilebilir bakiyeyi aşıyorsa zorunlu (backend `over_balance_note_required`). */
export interface AdminCreatePayoutRequest {
  currency: CurrencyCode;
  amount: number;
  status: "Approved" | "Paid";
  iban?: string;
  ibanOwnerName?: string;
  note?: string;
  /** YYYY-MM-DD, sadece `Paid` için. */
  paidOn?: string;
  bankReference?: string;
}

export interface AdminEditPayoutRequest {
  amount: number;
  iban: string;
  ibanOwnerName: string;
  note?: string;
}

export interface AdminMarkPayoutPaidRequest {
  /** YYYY-MM-DD */
  paidOn: string;
  bankReference: string;
}

export interface AdminRejectPayoutRequest {
  reason: string;
}

export interface AdminCreateAdjustmentRequest {
  currency: CurrencyCode;
  /** Pozitif organizatör lehine, negatif aleyhine. */
  amount: number;
  reason: string;
}

/** POST /admin/organizers/{id}/impersonate — sebep organizatörün işlem geçmişinde görünür. */
export interface AdminImpersonateRequest {
  reason: string;
}

/** Oturum çerezi yanıtla birlikte set edilir; `dashboardUrl` yeni sekmede açılır. */
export interface AdminImpersonationResponse {
  dashboardUrl: string;
  expiresAt: string;
}

export type AdminReportGroupBy = "Day" | "Week" | "Month";

/**
 * Sipariş ödeme tarihinin (Türkiye saati) dönemine yazılır. `grossSales` sonradan
 * tamamen iade edilenleri de içerir; `orderCount` içermez.
 */
export interface AdminSalesReportRow {
  /** YYYY-MM-DD, dönemin ilk günü (hafta Pazartesi). Toplam satırında `from`. */
  period: string;
  currency: CurrencyCode;
  orderCount: number;
  ticketCount: number;
  grossSales: number;
  refunded: number;
  netSales: number;
  commission: number;
  serviceFee: number;
  platformRevenue: number;
  organizerNet: number;
}

export interface AdminSalesReportResponse {
  from: string;
  to: string;
  groupBy: AdminReportGroupBy;
  rows: AdminSalesReportRow[];
  totals: AdminSalesReportRow[];
}

/** `total` = bekleyen + çekilebilir + süreçte. Eksi → avans, organizatör borçlu. */
export interface AdminLiabilityRow {
  organizerId: string;
  organizerName: string;
  currency: CurrencyCode;
  pending: number;
  available: number;
  inProgress: number;
  total: number;
}

export interface AdminLiabilityTotals {
  currency: CurrencyCode;
  pending: number;
  available: number;
  inProgress: number;
  total: number;
  organizerCount: number;
}

export interface AdminLiabilityResponse {
  generatedAt: string;
  totals: AdminLiabilityTotals[];
  organizers: AdminLiabilityRow[];
}

/** Bitmiş etkinlik; `snapshotTakenAt` null = rapor henüz üretilmedi. */
export interface AdminEventReportItem {
  eventId: string;
  title: string;
  endsAt: string;
  status: EventStatus;
  snapshotTakenAt: string | null;
  currency: CurrencyCode | null;
  ticketsSold: number | null;
  organizerNetRevenue: number | null;
}

export interface AdminEventReportListResponse {
  items: AdminEventReportItem[];
  total: number;
  page: number;
  pageSize: number;
}

export type PlatformAdminStatus = "Active" | "Disabled";

export interface AdminTeamMember {
  id: string;
  email: string;
  displayName: string;
  role: PlatformAdminRole;
  status: PlatformAdminStatus;
  /** false = davet edildi, şifresini henüz belirlemedi. */
  passwordSet: boolean;
  /** Sadece şifresi belirlenmemiş admin için; geçmişteyse davet yeniden gönderilmeli. */
  inviteExpiresAt: string | null;
  lastLoginAt: string | null;
  /** Açık admin oturumları (destek oturumları hariç). */
  activeSessionCount: number;
  createdAt: string;
}

/** POST /admin/team — 24 saatlik şifre belirleme linki e-postayla gider. */
export interface AdminInviteRequest {
  email: string;
  displayName: string;
  role: PlatformAdminRole;
}

/** Tüm platform admin işlemleri (giriş ve ekip işlemleri dahil). */
export interface AdminPlatformLogItem {
  id: string;
  action: string;
  actorAdminId: string | null;
  actorEmail: string;
  targetOrganizerId: string | null;
  targetOrganizerName: string | null;
  diff: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AdminPlatformLogResponse {
  items: AdminPlatformLogItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** Kayıt filtresi için; pasif admin'ler de gelir. */
export interface AdminLookupItem {
  id: string;
  email: string;
  displayName: string;
}

/** GET /admin/members — organizatör kullanıcıları (ekip üyeleri). */
export interface AdminMemberListItem {
  id: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
  organizerId: string;
  organizerName: string;
  organizerStatus: OrganizerStatus;
  /** false = daveti kabul etmedi, şifresi yok. */
  passwordSet: boolean;
  /** Organizatör hesabının (Owner'ın) e-posta doğrulaması. */
  emailVerified: boolean;
  inviteExpiresAt: string | null;
  joinedAt: string | null;
  lastLoginAt: string | null;
  /** Açık oturumlar; destek oturumları sayılmaz. */
  activeSessionCount: number;
  createdAt: string;
}

export interface AdminMemberListResponse {
  items: AdminMemberListItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** GET /admin/orders — bütün organizatörler, yeni → eski. */
export interface AdminOrderListItem {
  orderNumber: string;
  status: OrderStatus;
  organizerId: string;
  organizerName: string;
  eventId: string;
  eventTitle: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerEmail: string;
  total: number;
  currency: CurrencyCode;
  ticketCount: number;
  /** `PaymentProviderKey` veya "Free"; ödeme başlatılmadıysa null. */
  paymentProvider: string | null;
  createdAt: string;
  paidAt: string | null;
  /** Sorunlu ödeme listesinde görünür. */
  paymentAttention: boolean;
}

export interface AdminOrderListResponse {
  items: AdminOrderListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminOrderCheckIn {
  ticketId: string;
  usedAt: string;
  checkedInByEmail: string | null;
}

export interface AdminOrderActivityItem {
  action: string;
  /** Organizatörün gördüğü aktör; admin işlemlerinde "TicketNjoy". */
  actorEmail: string | null;
  /** İşlemi admin panelinden ya da destek oturumundan yapan admin. */
  adminEmail: string | null;
  diff: string | null;
  createdAt: string;
}

/** GET /admin/orders/{orderNumber} */
export interface AdminOrderDetail {
  order: OrderDetailResponse;
  organizerId: string;
  organizerName: string;
  organizerStatus: OrganizerStatus;
  eventId: string;
  channel: string;
  /** Bekleyen siparişin ödeme süresi; diğer durumlarda tarihsel değer. */
  expiresAt: string;
  cancelledAt: string | null;
  cancellationReason: string | null;
  commissionTotal: number;
  cardFeeTotal: number;
  organizerNetTotal: number;
  checkIns: AdminOrderCheckIn[];
  /** Organizatörün Aktivite kaydında bu siparişe ait satırlar, eski → yeni. */
  activity: AdminOrderActivityItem[];
  payment: AdminOrderPaymentIssue;
}

/** Banka "ödendi" dedi ama sipariş tamamlanamadı. */
export type OrderPaymentIssue = "captured_not_finalized";
export type OrderPaymentIssueResolution = "confirmed" | "refunded" | "dismissed";
/** provider_query: banka doğruladı · override: admin sebep yazarak onayladı. */
export type OrderManualConfirmation = "provider_query" | "override";

export interface AdminOrderPaymentIssue {
  /** Ödemenin başlatıldığı sağlayıcı; başlatılmadıysa null. */
  provider: string | null;
  transactionId: string | null;
  /** Karttan çekilmesi gereken tutar (taksit farkı dahil). */
  expectedAmount: number;
  issue: OrderPaymentIssue | null;
  issueAt: string | null;
  resolution: OrderPaymentIssueResolution | null;
  resolvedAt: string | null;
  manualConfirmation: OrderManualConfirmation | null;
  manuallyConfirmedAt: string | null;
  /** Sorunlu listesinde görünür. */
  attention: boolean;
  /** Elle onay / biletsiz iade yapılabilir. */
  recoverable: boolean;
}

export type PaymentQueryStatus = "Paid" | "NotPaid" | "Refunded" | "NotFound" | "Unsupported" | "Unknown";

/** POST /admin/orders/{orderNumber}/payment-query — süper admin. */
export interface AdminPaymentQueryResponse {
  status: PaymentQueryStatus;
  /** Sağlayıcının ham durum kodu (Param: SUCCESS, FAIL, REFUND…). */
  providerStatus: string | null;
  transactionId: string | null;
  amount: number | null;
  refundedAmount: number | null;
  expectedAmount: number;
  /** Elle onayın sebepsiz yapılabilmesi için banka tutarı beklenenle aynı olmalı. */
  amountMatches: boolean;
  installmentCount: number | null;
  message: string | null;
  queriedAt: string;
}

/** POST /admin/orders/{orderNumber}/confirm-payment — banka doğrulamadıysa override + sebep. */
export interface AdminConfirmPaymentRequest {
  override: boolean;
  reason: string | null;
}

/** PUT /admin/orders/{orderNumber}/buyer — biletler kendiliğinden yeniden gönderilmez. */
export interface AdminUpdateBuyerRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
